import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { ClientAppEvent } from "constants/index";
import { useAPI, useRoleContext } from "hooks";
import { getApiErrorMessage } from "utils";

import { deduplicateCatchAllLanes, generateDefaultLaneName } from "./utils";
import {
  createEmptyLaneForm,
  getLaneFormBlockedReason,
  laneFormToRequestBody,
} from "../../utils";
import { LaneFormFields } from "../LaneFormFields";

import type { LaneFormState } from "../../utils";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  onLanesCreated: VoidFunction;
}

export function CreateLanesModal(props: Props) {
  const { open, onClose, onLanesCreated } = props;
  const { role } = useRoleContext();
  const { api } = useAPI();
  const posthog = usePostHog();
  const [laneItems, setLaneItems] = useState<LaneFormState[]>([
    createEmptyLaneForm(),
  ]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleClose = (): void => {
    setIsSubmitting(false);
    setLaneItems([createEmptyLaneForm()]);
    onClose();
  };

  const handleAddLane = (): void => {
    setLaneItems((prev) => [...prev, createEmptyLaneForm()]);
  };

  const handleRemoveLane = (id: string): void => {
    setLaneItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleUpdateLane = (
    id: string,
    patch: Partial<LaneFormState>,
  ): void => {
    setLaneItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const submitLane = async (
    item: LaneFormState,
    roleId: number,
  ): Promise<{ item: LaneFormState; error: string | null }> => {
    const body = laneFormToRequestBody(
      item,
      item.name.trim() || generateDefaultLaneName(),
    );
    try {
      const response = await api<ShipmentSearch>(
        "post",
        `/carriers/${roleId}/lanes`,
        body,
      );
      if (body.expires_at) {
        posthog?.capture(ClientAppEvent.CARRIER_LANE_CREATED_WITH_EXPIRY, {
          lane_id: response.data.id,
          expires_at: body.expires_at,
        });
      }
      return { item, error: null };
    } catch (e: unknown) {
      return {
        item,
        error: getApiErrorMessage(e, "Failed to save lane."),
      };
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!role) return;
    setIsSubmitting(true);

    const itemsToSubmit = deduplicateCatchAllLanes(laneItems);
    setLaneItems(itemsToSubmit);
    const results = await itemsToSubmit.reduce(
      async (prevPromise, item) => {
        const acc = await prevPromise;
        const result = await submitLane(item, role.id);
        return [...acc, result];
      },
      Promise.resolve([] as { item: LaneFormState; error: string | null }[]),
    );

    const failed = results
      .filter((r) => r.error !== null)
      .map((r) => ({ ...r.item, error: r.error }));
    const anySucceeded = results.some((r) => r.error === null);

    if (anySucceeded) {
      onLanesCreated();
    }

    if (failed.length === 0) {
      handleClose();
    } else {
      setIsSubmitting(false);
      setLaneItems(failed);
    }
  };

  const saveBlockedReason = laneItems.reduce<string | null>(
    (reason, item) => reason ?? getLaneFormBlockedReason(item, false),
    null,
  );

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight="bold">
            Create Lanes
          </Typography>
          <Tooltip title="Close">
            <span>
              <IconButton
                onClick={handleClose}
                size="small"
                disabled={isSubmitting}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create lanes where you most often want/need freight. Evotrux will
          automatically notify you the instant matching loads are posted.
        </Typography>
        <Stack spacing={3} divider={<Divider />}>
          {laneItems.map((item) => (
            <Box key={item.id}>
              {laneItems.length > 1 && (
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  sx={{ mb: 0.5 }}
                >
                  <Tooltip title="Remove lane">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveLane(item.id)}
                      aria-label="Remove lane"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
              <LaneFormFields
                value={item}
                onChange={(patch) => handleUpdateLane(item.id, patch)}
              />
              {item.error && <ErrorTypography error={item.error} />}
            </Box>
          ))}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddLane}
          disabled={isSubmitting}
          sx={{ mt: 2, textTransform: "none", pl: 0 }}
        >
          Add another lane
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <LoadingWrappedNode loading={isSubmitting}>
          <>
            <Tooltip title="Close without creating any lanes">
              <Button onClick={handleClose}>CANCEL</Button>
            </Tooltip>
            <Tooltip
              title={
                saveBlockedReason ??
                "Create these lanes and start matching loads"
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={Boolean(saveBlockedReason)}
                >
                  SAVE LANES
                </Button>
              </span>
            </Tooltip>
          </>
        </LoadingWrappedNode>
      </DialogActions>
    </Dialog>
  );
}
